import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import Modal from '@/components/common/Modal';
import { showToast } from '@/store/ducks/toasts';
import AnnotationIcon from '@/svg-icons/annotation';
import EditIcon from '@/components/common/icons/edit.svg';
import renderSvg from '@/utils/renderSvg';

const AnnotationSection = ({ annotation, orderId, orderCode, inspectionIcon, refreshHandler, orderPage }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const saveAnnotation = async () => {
    setLoading(true);
    const formData = new FormData(document.getElementById('annotationForm'));
    const formValues = {
      description: formData.get('description'),
    };
    try {
      await API.patch(`orders/${orderId}/annotation`, formValues);
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'ANNOTATION_UPDATED_SUCCESS' }),
          duration: 5000,
        })
      );
      closeModal();
      if (refreshHandler) {
        refreshHandler();
      }
    } catch (err) {
      console.debug('AnnotationSection saveAnnotation error ', err?.response || err);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = annotation?.date ? format(new Date(annotation.date), 'dd/MM/yyyy') : '';
  const formattedTime = annotation?.date ? format(new Date(annotation.date), "HH'h'mm") : '';

  return (
    <div className="w-full">
      <div className="flex justify-between font-bold text-[calc(6px+0.4vw)] my-[0.6vw]">
        <div className="flex items-center">
          {orderPage && (
            <AnnotationIcon viewBox="0 0 25 25" className="text-gray-700 mr-2 w-6 h-6 shrink-0 align-middle" />
          )}
          <FormattedMessage id="ANNOTATION" />
        </div>

        <button
          className="p-0 border-none bg-white leading-none cursor-pointer hover:opacity-75 focus:outline-none"
          onClick={openModal}
        >
          {renderSvg(EditIcon, { className: 'w-5 h-5', alt: '' })}
        </button>
      </div>

      {orderPage && annotation?.date && (
        <div className="text-[calc(6px+0.4vw)] text-right text-[#A5A5A5] mt-[0.5vw] mb-0 font-bold">
          Editado: {`${formattedDate} - ${formattedTime}`}
        </div>
      )}

      <div
        className={`text-[#858585] text-[calc(6px+0.4vw)] my-[1vw] whitespace-pre-wrap ${
          orderPage ? 'pl-[calc(14px+1.8vw)] mt-[0.5vw]' : ''
        }`}
      >
        {annotation?.description}
      </div>

      {showModal && (
        <Modal onClose={closeModal} extraStyles={{ maxWidth: '425px' }}>
          <div className="p-1">
            <div className="flex items-center gap-[5px] text-[18px] font-semibold text-[#393939] mb-3">
              {inspectionIcon && <span className="flex items-center shrink-0">{inspectionIcon}</span>}
              <span>{orderCode}</span>
            </div>

            <hr className="border-gray-200 my-3" />
            
            <form id="annotationForm" onSubmit={e => { e.preventDefault(); saveAnnotation(); }}>
              <div>
                <div className="flex flex-col mb-[40px]">
                  <div className="flex items-center mb-3">
                    <AnnotationIcon viewBox="0 0 25 25" className="text-[#393939] w-[25px] h-[25px] shrink-0 mr-[1vw]" />
                    <span className="text-[14px] text-[#393939] font-bold" style={{ textTransform: 'none' }}>
                      <FormattedMessage id="ANNOTATION" />
                    </span>
                  </div>
                  <textarea
                    id="description"
                    name="description"
                    defaultValue={annotation?.description || ''}
                    placeholder="Registrar anotação..."
                    className="w-full h-[120px] p-[5px] border border-[#cccccc] rounded-[4px] text-[14px] text-[#393939] placeholder:text-[#b1b1b1] focus:outline-none focus:border-[#5181C1]"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    className="inline-flex items-center justify-center min-w-[140px] h-[36px] px-[1.2rem] border-0 rounded-[3px] bg-[#c90b0b] text-white text-[0.9375rem] font-bold leading-[1.4] transition-opacity hover:opacity-85"
                    type="button"
                    onClick={closeModal}
                  >
                    <FormattedMessage id="BUTTON.CANCEL" />
                  </button>
                  
                  <button
                    className="inline-flex items-center justify-center min-w-[140px] h-[36px] px-[1.2rem] border-0 rounded-[3px] bg-[#97a41f] text-white text-[0.9375rem] font-bold leading-[1.4] transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-50"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-[18px] h-[18px] border-2 border-white/15 border-r-white/40 rounded-full animate-spin"></div>
                    ) : (
                      <FormattedMessage id="BUTTON.CONFIRM" />
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};

AnnotationSection.propTypes = {
  orderPage: PropTypes.bool,
  annotation: PropTypes.object.isRequired,
  orderId: PropTypes.string.isRequired,
  orderCode: PropTypes.string.isRequired,
  inspectionIcon: PropTypes.object,
  refreshHandler: PropTypes.func,
};

export default AnnotationSection;
